import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Phone, Users, Bell, CircleDot, CheckSquare, Square, Trash2, Lightbulb, ChevronDown, ChevronUp, X, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  call:       { label: "Call",       icon: Phone,      color: "text-blue-600",   bg: "bg-blue-50"  },
  follow_up:  { label: "Follow-up",  icon: Users,      color: "text-purple-600", bg: "bg-purple-50"},
  reminder:   { label: "Reminder",   icon: Bell,       color: "text-yellow-600", bg: "bg-yellow-50"},
  other:      { label: "Other",      icon: CircleDot,  color: "text-gray-500",   bg: "bg-gray-50"  },
};
const PRIORITY_META = {
  high:   { label: "High",   cls: "bg-red-100 text-red-700 border-red-200"    },
  medium: { label: "Medium", cls: "bg-yellow-100 text-yellow-700 border-yellow-200" },
  low:    { label: "Low",    cls: "bg-gray-100 text-gray-600 border-gray-200"  },
};

const today = () => new Date().toISOString().split("T")[0];
const isOverdue = (t) => t.status === "pending" && t.due_date < today();

function groupTasks(tasks) {
  const todayStr = today();
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split("T")[0];
  const weekEnd = new Date(); weekEnd.setDate(weekEnd.getDate() + 7);
  const weekStr = weekEnd.toISOString().split("T")[0];

  const groups = { overdue: [], today: [], tomorrow: [], week: [], later: [], done: [] };
  tasks.forEach((t) => {
    if (t.status === "done") { groups.done.push(t); return; }
    if (t.due_date < todayStr) { groups.overdue.push(t); return; }
    if (t.due_date === todayStr) { groups.today.push(t); return; }
    if (t.due_date === tomorrowStr) { groups.tomorrow.push(t); return; }
    if (t.due_date <= weekStr) { groups.week.push(t); return; }
    groups.later.push(t);
  });
  // Sort each group: priority (high→medium→low) then date
  const priOrder = { high: 1, medium: 2, low: 3 };
  Object.keys(groups).forEach((key) => {
    groups[key].sort((a, b) =>
      (priOrder[a.priority] || 2) - (priOrder[b.priority] || 2) ||
      a.due_date.localeCompare(b.due_date)
    );
  });
  return groups;
}

const emptyForm = { title: "", task_type: "reminder", client_id: "", client_name: "", due_date: today(), priority: "medium", notes: "" };

export default function TasksPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDone, setShowDone] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [tasksRes, sugRes, clientsRes] = await Promise.all([
        axios.get(`${API}/tasks`, { withCredentials: true }),
        axios.get(`${API}/tasks/suggestions`, { withCredentials: true }),
        axios.get(`${API}/crm/clients`, { withCredentials: true }),
      ]);
      setTasks(tasksRes.data);
      setSuggestions(sugRes.data);
      setClients(clientsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Pre-fill client from URL param
  useEffect(() => {
    const clientId = searchParams.get("client_id");
    const clientName = searchParams.get("client_name");
    if (clientId) {
      setForm((f) => ({ ...f, client_id: clientId, client_name: clientName || "" }));
      setDialogOpen(true);
    }
  }, [searchParams]);

  const openCreate = (prefill = {}) => {
    setEditingTask(null);
    setForm({ ...emptyForm, ...prefill });
    setDialogOpen(true);
  };

  const openEdit = (task) => {
    setEditingTask(task);
    setForm({ title: task.title, task_type: task.task_type, client_id: task.client_id || "", client_name: task.client_name || "", due_date: task.due_date, priority: task.priority, notes: task.notes || "" });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.due_date) { toast.error("Due date is required"); return; }
    setSaving(true);
    try {
      const body = { ...form, client_id: form.client_id || null, client_name: form.client_name || null, notes: form.notes || null };
      if (editingTask) {
        await axios.put(`${API}/tasks/${editingTask.id}`, body, { withCredentials: true });
        toast.success("Task updated");
      } else {
        await axios.post(`${API}/tasks`, body, { withCredentials: true });
        toast.success("Task created");
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      let msg;
      if (status === 401) msg = "[401] Sesión expirada — recargue la página e inicie sesión de nuevo";
      else if (typeof detail === "string") msg = `[${status || "ERR"}] ${detail}`;
      else if (Array.isArray(detail)) msg = `[${status}] ` + detail.map((d) => d.msg || JSON.stringify(d)).join(" ");
      else if (err.message) msg = `Network/Server: ${err.message}`;
      else msg = "Error saving task";
      toast.error(msg);
      // eslint-disable-next-line no-console
      console.error("Task save failed:", { status, detail, error: err });
    } finally {
      setSaving(false);
    }
  };

  const toggleComplete = async (task) => {
    try {
      if (task.status === "done") {
        await axios.post(`${API}/tasks/${task.id}/reopen`, {}, { withCredentials: true });
      } else {
        await axios.post(`${API}/tasks/${task.id}/complete`, {}, { withCredentials: true });
      }
      fetchAll();
    } catch { toast.error("Failed to update task"); }
  };

  const deleteTask = async (id) => {
    try {
      await axios.delete(`${API}/tasks/${id}`, { withCredentials: true });
      toast.success("Task deleted");
      fetchAll();
    } catch { toast.error("Failed to delete"); }
  };

  const createFromSuggestion = (s) => {
    openCreate({ task_type: "follow_up", client_id: s.client_id, client_name: s.client_name, title: `Follow-up: ${s.client_name}`, priority: "medium" });
  };

  const handleClientChange = (clientId) => {
    const c = clients.find((cl) => cl.id === clientId);
    setForm((f) => ({ ...f, client_id: clientId, client_name: c?.name || "" }));
  };

  const groups = groupTasks(tasks);
  const pendingCount = tasks.filter((t) => t.status === "pending").length;

  const renderTask = (task, highlight = false) => {
    const meta = TYPE_META[task.task_type] || TYPE_META.other;
    const Icon = meta.icon;
    const pri = PRIORITY_META[task.priority] || PRIORITY_META.medium;
    const done = task.status === "done";
    return (
      <div
        key={task.id}
        data-testid={`task-row-${task.id}`}
        className={`flex items-start gap-3 px-4 py-3 border-b border-gray-100 last:border-0 group transition-colors ${
          highlight ? "bg-red-50 border-l-4 border-l-red-400 pl-3" :
          done ? "bg-gray-50 opacity-60" : "hover:bg-gray-50"
        }`}
      >
        <button onClick={() => toggleComplete(task)} data-testid={`task-toggle-${task.id}`} className="mt-0.5 flex-shrink-0">
          {done ? <CheckSquare className="w-5 h-5 text-green-500" /> : <Square className="w-5 h-5 text-gray-300 hover:text-gray-500" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-medium ${done ? "line-through text-gray-400" : "text-gray-900"}`}>{task.title}</span>
            {highlight && <span className="text-[9px] font-bold bg-red-500 text-white px-1.5 py-0.5 rounded uppercase tracking-wide">Overdue</span>}
            {task.source === "automation" && <span className="text-[9px] font-bold bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded uppercase tracking-wide">Auto</span>}
            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${pri.cls}`}>{pri.label}</span>
          </div>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${meta.color}`}>
              <Icon className="w-3 h-3" /> {meta.label}
            </span>
            {task.client_name && (
              <button
                onClick={() => task.client_id && navigate(`/admin/crm/clients/${task.client_id}`)}
                className="text-[11px] text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-0.5 font-medium"
              >
                {task.client_name} <ExternalLink className="w-2.5 h-2.5" />
              </button>
            )}
            <span className={`text-[11px] ${highlight ? "text-red-600 font-bold" : "text-gray-400"}`}>
              {task.due_date}
            </span>
          </div>
          {task.notes && <p className="text-xs text-gray-400 mt-1 truncate">{task.notes}</p>}
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          <button onClick={() => openEdit(task)} className="p-1 text-gray-400 hover:text-gray-700 rounded-sm hover:bg-gray-100">
            <CircleDot className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => deleteTask(task.id)} data-testid={`task-delete-${task.id}`} className="p-1 text-gray-400 hover:text-red-600 rounded-sm hover:bg-red-50">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  };

  const GroupSection = ({ label, tasks: groupTasks, color = "text-gray-700", highlight = false }) => {
    if (!groupTasks.length) return null;
    return (
      <div className="mb-4">
        <div className={`px-4 py-1.5 bg-gray-50 border-y border-gray-100 text-xs font-bold uppercase tracking-wide ${color}`}>
          {label} <span className="font-normal opacity-60">({groupTasks.length})</span>
        </div>
        {groupTasks.map((t) => renderTask(t, highlight))}
      </div>
    );
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div data-testid="tasks-page" className="p-8 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">My Tasks</h1>
          <p className="text-sm text-gray-500 mt-1">{pendingCount} pending · {tasks.filter((t) => t.status === "done").length} done</p>
        </div>
        <button
          data-testid="new-task-btn"
          onClick={() => openCreate()}
          className="inline-flex items-center gap-2 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 transition-colors px-4 py-2 rounded-sm text-sm"
        >
          <Plus className="w-4 h-4" /> New Task
        </button>
      </div>

      {/* Auto-suggestions */}
      {suggestions.length > 0 && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-sm overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-blue-800"
            onClick={() => setShowSuggestions((v) => !v)}
          >
            <span className="flex items-center gap-2"><Lightbulb className="w-4 h-4" /> Suggested follow-ups ({suggestions.length})</span>
            {showSuggestions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showSuggestions && (
            <div className="border-t border-blue-200">
              {suggestions.map((s) => (
                <div key={s.client_id} className="flex items-center justify-between px-4 py-2.5 border-b border-blue-100 last:border-0 bg-white">
                  <div>
                    <span className="text-sm font-medium text-gray-900">{s.client_name}</span>
                    <span className="text-xs text-gray-500 ml-2">
                      {s.days_since_contact != null ? `${s.days_since_contact} days without contact` : "Never contacted"}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => navigate(`/admin/crm/clients/${s.client_id}`)} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 border border-gray-200 rounded-sm hover:bg-gray-50">
                      View
                    </button>
                    <button
                      onClick={() => createFromSuggestion(s)}
                      data-testid={`suggestion-add-${s.client_id}`}
                      className="text-xs font-semibold text-blue-700 hover:text-blue-900 px-2 py-1 bg-blue-100 rounded-sm hover:bg-blue-200"
                    >
                      + Task
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Task list */}
      <div className="bg-white border border-gray-200 rounded-sm">
        {groups.overdue.length === 0 && groups.today.length === 0 && groups.tomorrow.length === 0 && groups.week.length === 0 && groups.later.length === 0 && groups.done.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <CheckSquare className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No tasks yet.</p>
            <button onClick={() => openCreate()} className="text-sm text-yellow-600 hover:underline mt-2">Create your first task</button>
          </div>
        )}
        <GroupSection label="Overdue" tasks={groups.overdue} color="text-red-600" highlight />
        <GroupSection label="Today" tasks={groups.today} color="text-gray-900" />
        <GroupSection label="Tomorrow" tasks={groups.tomorrow} />
        <GroupSection label="This Week" tasks={groups.week} />
        <GroupSection label="Later" tasks={groups.later} />

        {groups.done.length > 0 && (
          <div>
            <button
              onClick={() => setShowDone((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 border-t border-gray-100 text-xs font-bold uppercase tracking-wide text-gray-400"
            >
              <span>Completed ({groups.done.length})</span>
              {showDone ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {showDone && groups.done.map((t) => renderTask(t))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTask ? "Edit Task" : "New Task"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Title *</label>
              <Input data-testid="task-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Call Jean Tremblay about the Toyota" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1 block">Type</label>
                <Select value={form.task_type} onValueChange={(v) => setForm((f) => ({ ...f, task_type: v }))}>
                  <SelectTrigger data-testid="task-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="call">Call</SelectItem>
                    <SelectItem value="follow_up">Follow-up</SelectItem>
                    <SelectItem value="reminder">Reminder</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1 block">Priority</label>
                <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                  <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Due Date *</label>
              <Input type="date" data-testid="task-date-input" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Linked Client (optional)</label>
              <Select value={form.client_id || "none"} onValueChange={(v) => v === "none" ? setForm((f) => ({ ...f, client_id: "", client_name: "" })) : handleClientChange(v)}>
                <SelectTrigger data-testid="task-client-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Notes (optional)</label>
              <Textarea data-testid="task-notes-input" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Any details..." className="min-h-[70px] text-sm" />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setDialogOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-sm">Cancel</button>
            <button data-testid="task-save-btn" onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm font-bold bg-yellow-400 text-gray-900 hover:bg-yellow-500 rounded-sm disabled:opacity-50">
              {saving ? "Saving..." : editingTask ? "Update" : "Create Task"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
